import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import VipIcon from '../Icons/VipIcons';
import { Button, SecondaryButton, DangerButton, IconButton } from './Button';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000001; /* Higher than Header's ModalOverlay (10000) */
  padding: 20px;
  box-sizing: border-box;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: ${props => props.theme.colors.bg};
  border-radius: 12px;
  padding: ${props => props.padding || '24px'};
  max-width: ${props => props.maxWidth || '500px'};
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideIn 0.3s ease;
  background: ${({theme}) => theme.colors.background};
  
  @keyframes slideIn {
    from { 
      opacity: 0; 
      transform: scale(0.9) translateY(-20px); 
    }
    to { 
      opacity: 1; 
      transform: scale(1) translateY(0); 
    }
  }
  
  @media (max-width: 768px) {
    margin: 10px;
    max-height: calc(100vh - 40px);
    padding: 14px;
  }
`;

const CloseButton = styled(IconButton).attrs({ $size: 'small' })`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.colors.textSecondary};
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
  }
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.bg2};
    color: ${props => props.theme.colors.text};
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;

const ModalHeader = styled.div`
  margin-bottom: 20px;
  padding-right: 40px; /* Space for close button */
  
  @media (max-width: 768px) {
    margin-bottom: 12px;
    padding-right: 32px;
  }
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ModalSubtitle = styled.p`
  margin: 8px 0 0 0;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 14px;
`;

const ModalBody = styled.div`
  margin-bottom: ${props => props.hasFooter ? '20px' : '0'};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid ${props => props.theme.colors.border};
  
  @media (max-width: 480px) {
    flex-direction: column-reverse;
    
    button {
      width: 100%;
    }
  }
`;

export const Modal = ({ 
  isOpen, 
  onClose, 
  children, 
  maxWidth,
  padding,
  title,
  subtitle,
  footer,
  closeOnOverlayClick = true,
  showCloseButton = true
}) => {
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent maxWidth={maxWidth} padding={padding}>
        {showCloseButton && (
          <CloseButton onClick={onClose} aria-label="Close">
            <VipIcon name="CloseXCircle" size={24} />
          </CloseButton>
        )}
        
        {(title || subtitle) && (
          <ModalHeader>
            {title && <ModalTitle>{title}</ModalTitle>}
            {subtitle && <ModalSubtitle>{subtitle}</ModalSubtitle>}
          </ModalHeader>
        )}
        
        <ModalBody hasFooter={!!footer}>
          {children}
        </ModalBody>
        
        {footer && (
          <ModalFooter>
            {footer}
          </ModalFooter>
        )}
      </ModalContent>
    </ModalOverlay>
  );

  // Render the modal at the document body level using a portal
  return ReactDOM.createPortal(modalContent, document.body);
};

// Convenience component for confirmation dialogs
export const ConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm,
  title = "Confirm Action",
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  hideCancel = false,
  danger = false,
  loading = false
}) => {
  const handleConfirm = async () => {
    if (!loading) {
      await onConfirm();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={loading ? undefined : onClose}
      title={title}
      maxWidth="400px"
      closeOnOverlayClick={!loading}
      showCloseButton={!loading}
      footer={
        <>
          {!hideCancel && (
            <SecondaryButton 
              onClick={onClose}
              disabled={loading}
              $rounded
            >
              {cancelText}
            </SecondaryButton>
          )}
          {danger ? (
            <DangerButton 
              onClick={handleConfirm}
              disabled={loading}
              $loading={loading}
              $rounded
            >
              {confirmText}
            </DangerButton>
          ) : (
            <Button 
              onClick={handleConfirm}
              disabled={loading}
              $loading={loading}
              $rounded
            >
              {confirmText}
            </Button>
          )}
        </>
      }
    >
      <p style={{ margin: '0', lineHeight: '1.5' }}>{message}</p>
    </Modal>
  );
};
